import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";

import api from "../../utils/api/axios";
import {
  getCategoryBreadCrumbs,
  getMainCategoryColor,
} from "../../utils/categories";
import {
  getOldCategoryRedirectionOffline,
  getOldSheetRedirectionOffline,
  getOldCategoryRedirectionOnline,
  getOldSheetRedirectionOnline,
} from "../../utils/legacy";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { GetSheet, PlanPart, Sheet, SheetPart } from "../../types/api/sheet";
import { Category } from "../../types/api/category";

import ArrowRightTailed from "../../assets/icons/arrows/right-tailed";
import key from "../../assets/icons/key.svg";

import CustomBreadcrumbs from "../../components/CustomBreadcrumbs";
import Loader, { StyledCircularProgress } from "../../components/Loader";
import NoData from "../../components/NoData";
import Part from "../../components/sheet/Part";
import CustomModal from "../../components/CustomModal";

import "./Sheet.css";
import { get, set } from "idb-keyval";
import { AxiosError } from "axios";
import { setHasNoNetwork } from "../../store/reducers/auth.slice";
import Minus from "../../assets/icons/minus";
import Plus from "../../assets/icons/plus";
import { DecisionTree } from "../../types/api/decision-tree";

interface PlanPartState extends PlanPart {
  isOpen?: boolean;
}

const DECISION_TREES_PART_ID = 9999;

const shouldDisplayDecisionTrees = (
  partName: string,
  mainCategory?: Category
) => {
  if (!mainCategory) return false;
  return (
    (mainCategory.id === 1 && partName === "Traitement") ||
    (mainCategory.id === 3 && partName === "Administration")
  );
};

const SheetPage = () => {
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width: 1024px)");

  const dispatch = useAppDispatch();

  const auth = useAppSelector((state) => state.auth);
  const { user, isAuth } = auth;
  const isEstablishment = user?.role === "ESTABLISHMENT";

  const { sheetId } = useParams();
  const [searchParams] = useSearchParams();
  const legacySheetId = searchParams.get("id");
  const legacyCatId = searchParams.get("cat_id");

  const [isLoading, setIsLoading] = useState(true);
  const [color, setColor] = useState<string>("navy");
  const [visibleIndex, setVisibleIndex] = useState<number>();
  const [mainCategoryName, setMainCategoryName] = useState<string>();
  const [sheet, setSheet] = useState<Sheet>();
  const [sheetParts, setSheetParts] = useState<SheetPart[]>([]);
  const [planParts, setPlanParts] = useState<PlanPartState[]>([]);
  const [categories, setCategories] = useState<Category>();
  const [hasNoAccess, setHasNoAccess] = useState<boolean>(false);
  const sheetPartsRefs: { [key: number]: HTMLElement | null } = {};
  const [decisionTrees, setDecisionTrees] = useState<DecisionTree[]>([]);
  const [decisionTreesOpen, setDecisionTreesOpen] = useState(false);
  const [mainCategory, setMainCategory] = useState<Category>();
  const scrollToPart = (id: number, decisionTrees = false) => {
    if (decisionTrees) {
      setDecisionTreesOpen(true);
      setPlanParts(
        planParts.map((part) => ({
          ...part,
          isOpen: false,
        }))
      );
    } else {
      setDecisionTreesOpen(false);
      setPlanParts(
        planParts.map((part) => ({
          ...part,
          isOpen: part.id === id ? true : false,
        }))
      );
    }
    const part = sheetPartsRefs[decisionTrees ? DECISION_TREES_PART_ID : id];
    if (part) {
      const top = part.offsetTop;
      const topOffset = top - 80;
      window.scrollTo({ top: topOffset, behavior: "smooth" });
    }
  };

  const onScroll = () => {
    const entries = Object.entries(sheetPartsRefs);
    let closestToTop: [string, HTMLElement | null] | null = null;
    let closestDistance = Infinity;

    entries.forEach(([key, ref]) => {
      if (ref) {
        const rect = ref.getBoundingClientRect();
        // Consider an element visible if it's within the viewport or just above it
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          // Calculate distance from the top of the viewport (with some offset)
          const distanceFromTop = Math.abs(rect.top - 80); // 80px offset for header
          if (distanceFromTop < closestDistance) {
            closestDistance = distanceFromTop;
            closestToTop = [key, ref];
          }
        }
      }
    });

    if (closestToTop) {
      const visibleIndex = parseInt(closestToTop[0]);
      setVisibleIndex(visibleIndex);
    }
  };

  const getRedirection = async (
    legacyCategoryId: string,
    legacySheetId: string | null
  ) => {
    try {
      if (legacySheetId) {
        try {
          const sheetId = await getOldSheetRedirectionOffline(
            legacySheetId,
            legacyCategoryId
          );
          if (sheetId) navigate(`/fiche/${sheetId}`);
        } catch (e) {
          const sheetIdOnline = await getOldSheetRedirectionOnline(
            legacySheetId,
            legacyCategoryId
          );
          if (sheetIdOnline) navigate(`/fiche/${sheetIdOnline}`);
          else navigate(-1);
        }
      } else {
        try {
          const categoryId = await getOldCategoryRedirectionOffline(
            legacyCategoryId
          );
          if (categoryId) navigate(`/categorie/${categoryId}`);
        } catch (e) {
          const categoryIdOnline = await getOldCategoryRedirectionOnline(
            legacyCategoryId
          );
          if (categoryIdOnline) navigate(`/categorie/${categoryIdOnline}`);
          else navigate(-1);
        }
      }
    } catch (_) {}
  };

  const displaySheet = useCallback((sheetInfos: GetSheet) => {
    const parts = sheetInfos.planParts.reduce(
      (acc, planPart) => {
        const sheetPart = sheetInfos.sheetParts?.find(
          (p) => p.planId === planPart.id
        );
        if (sheetPart && sheetPart.content !== null && planPart.isActive) {
          acc.push({ sheetPart, planPart });
        }
        return acc;
      },
      [] as {
        sheetPart: SheetPart;
        planPart: PlanPart;
      }[]
    );
    const newSheetParts = parts.map((part) => part.sheetPart);
    const newPlanParts = parts.map((part) => part.planPart);

    setSheet(sheetInfos.sheet);
    setColor(getMainCategoryColor(sheetInfos.mainCategory.id));
    setMainCategoryName(sheetInfos.mainCategory.name);
    setSheetParts(newSheetParts);
    setPlanParts(newPlanParts);
    setVisibleIndex(newPlanParts[0] ? newPlanParts[0].id : undefined);
    setCategories(sheetInfos.categories);
    setHasNoAccess(sheetInfos.hasAllowedAccess === false);
    setMainCategory(sheetInfos.mainCategory);
    if (sheetInfos.mainCategory.id === 1 && sheetInfos.decisionTrees) {
      setDecisionTrees(
        sheetInfos.decisionTrees.filter(
          (tree) => tree.treeType === "PRESCRIPTION"
        )
      );
    } else if (sheetInfos.mainCategory.id === 3 && sheetInfos.decisionTrees) {
      setDecisionTrees(
        sheetInfos.decisionTrees.filter(
          (tree) => tree.treeType === "ADMINISTRATION"
        )
      );
    } else {
      setDecisionTrees([]);
    }
  }, []);

  const saveSheet = useCallback(async (sheetInfos: GetSheet) => {
    await set("sheet-" + sheetInfos.sheet.id, sheetInfos);
  }, []);

  const fetchDataOnline = useCallback(
    async (sheetIdNumber: number) => {
      const { data: sheetInfos } = await api.get<GetSheet>(
        `/sheet/pwa/${sheetIdNumber}`
      );

      saveSheet(sheetInfos);
      displaySheet(sheetInfos);
    },
    [saveSheet, displaySheet]
  );

  const fetchDataOffline = useCallback(
    async (sheetIdNumber: number): Promise<boolean> => {
      const sheetInfos = await get<GetSheet>("sheet-" + sheetIdNumber);
      if (!sheetInfos) return false;
      displaySheet(sheetInfos);
      return true;
    },
    [displaySheet]
  );

  const fetchData = useCallback(async () => {
    if (legacyCatId || legacySheetId) return;
    if (!sheetId) return;
    const sheetIdNumber = parseInt(sheetId ?? "0");
    setIsLoading(true);
    const offlineSuccess = await fetchDataOffline(sheetIdNumber);
    if (offlineSuccess) {
      setIsLoading(false);
    }

    try {
      await fetchDataOnline(sheetIdNumber);
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.status === 500) dispatch(setHasNoNetwork(true));
      }
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (legacyCatId) getRedirection(legacyCatId, legacySheetId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacySheetId, legacyCatId]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetPartsRefs]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const uniquePlan =
    planParts.length === 1 && planParts[0].name === "(Partie unique générale)";
  return (
    <div className="sheet-container">
      {!uniquePlan && (
        <div className="sheet-plan">
          {planParts.map((part) => (
            <>
              <div
                key={part.id}
                className={[
                  "sheet-plan__part",
                  visibleIndex === part.id ? "visible" : "",
                ].join(" ")}
                onClick={() => scrollToPart(part.id)}
              >
                <p className={`sheet-plan__part--text text-${color}`}>
                  {part.name}
                </p>
                <ArrowRightTailed
                  className={"sheet-plan__part--img"}
                  color={color}
                />
              </div>
              {decisionTrees.length > 0 &&
                shouldDisplayDecisionTrees(part.name, mainCategory) && (
                  <div
                    key={DECISION_TREES_PART_ID}
                    className={[
                      "sheet-plan__part",
                      visibleIndex === DECISION_TREES_PART_ID ? "visible" : "",
                    ].join(" ")}
                    onClick={() => scrollToPart(part.id, true)}
                  >
                    <p className={`sheet-plan__part--text text-${color}`}>
                      {decisionTrees[0].treeType === "ADMINISTRATION"
                        ? "Aide à l'administration"
                        : "Aide à la prescription"}
                    </p>
                    <ArrowRightTailed
                      className={"sheet-plan__part--img"}
                      color={color}
                    />
                  </div>
                )}
            </>
          ))}
        </div>
      )}

      <div className="sheet-content">
        <CustomBreadcrumbs
          links={
            categories && sheet
              ? [...getCategoryBreadCrumbs(categories), { text: sheet.name }]
              : []
          }
        />

        <div className="sheet-title__container">
          {isLoading ? <StyledCircularProgress size={30} /> : null}
          {sheet && (
            <>
              <p className={`sheet-main-category background-${color}`}>
                {mainCategoryName}
              </p>
              <div className="sheet-title__content">
                <h1 className={["sheet-title"].join(" ")}>{sheet.name}</h1>
                <p className="sheet-date">
                  {dayjs(sheet.updatedAt).format("[mise à jour le] DD/MM/YYYY")}
                </p>
              </div>
            </>
          )}
        </div>

        <div className="sheet-parts-container">
          {isLoading ? (
            <Loader />
          ) : !sheetParts.length ? (
            <NoData />
          ) : (
            <>
              {sheetParts.map((part, index) => {
                const planPart = planParts[index];
                return (
                  <>
                    <div
                      key={part.id}
                      ref={(ref) => (sheetPartsRefs[part.planId] = ref)}
                    >
                      <Part
                        defaultOpen={
                          (!isSmallScreen && index === 0) || planPart.isOpen
                        }
                        name={planPart.name}
                        content={part.content as string}
                        color={color}
                      />
                    </div>
                    {decisionTrees.length > 0 &&
                      shouldDisplayDecisionTrees(
                        planPart.name,
                        mainCategory
                      ) && (
                        <div
                          key={`${DECISION_TREES_PART_ID}-trees-part`}
                          ref={(ref) =>
                            (sheetPartsRefs[DECISION_TREES_PART_ID] = ref)
                          }
                        >
                          <div className="sheet-part">
                            <div
                              className="sheet-part-title__container"
                              onClick={() =>
                                setDecisionTreesOpen(!decisionTreesOpen)
                              }
                            >
                              <p
                                className={["sheet-part-title", color].join(
                                  " "
                                )}
                              >
                                {decisionTrees[0].treeType === "ADMINISTRATION"
                                  ? "Aide à l'administration"
                                  : "Aide à la prescription"}
                              </p>
                              {decisionTreesOpen && <Minus color={color} />}
                              {!decisionTreesOpen && <Plus color={color} />}
                            </div>
                            {decisionTreesOpen && (
                              <div
                                className={`sheet-part__content li-${color}`}
                              >
                                {decisionTrees.map((tree) => (
                                  <li
                                    className={`${color}`}
                                    style={{
                                      cursor: "pointer",
                                      marginBottom: 10,
                                    }}
                                    onClick={() =>
                                      navigate(
                                        `/${
                                          tree.treeType === "ADMINISTRATION"
                                            ? "aide-administration"
                                            : "aide-prescription"
                                        }/${tree.id}`
                                      )
                                    }
                                  >
                                    {tree.name}
                                  </li>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                  </>
                );
              })}
            </>
          )}
        </div>
      </div>

      <CustomModal open={hasNoAccess || (hasNoAccess && !isAuth)} size="small">
        <div className="sheet-modal__title--container">
          <img className="sheet-modal__title--icon" src={key} alt="icon clé" />
          <p className="sheet-modal__title">Acces premium</p>
        </div>

        <p className="sheet-modal__text">
          {isEstablishment
            ? "Votre établissement n'a pas accès à cette fiche"
            : isAuth
            ? "Vous n'avez pas d'abonnement actif"
            : "Pour accéder à cette fiche, veuillez vous identifier"}
        </p>

        <div className="sheet-modal__button--container">
          {!isAuth ? (
            <>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/connexion/inscription")}
              >
                S'inscrire
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/connexion/se-connecter")}
              >
                Se connecter
              </Button>
            </>
          ) : isEstablishment ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/")}
            >
              Revenir à l'accueil
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/mon-compte/abonnement")}
            >
              Gérer mon abonnement
            </Button>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default SheetPage;
